import * as React from 'react';
import { useCallback, memo, useMemo } from 'react';
import { BarsArrowDownIcon } from '@heroicons/react/24/outline';
import tw from 'twin.macro';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { BaseModal, CustomDropdown, Paragraph, RadioGroup, Title } from '@restworld/ui-ds';
import { useModal, useResponsiveBreakPoints } from '@restworld/utils-common';

import { jobListParamsAtom, jobSortFilterAtom } from './job-explorer-list.atoms';

export const SortDropdown = ({ disabled }: { disabled?: boolean }) => {
  const { show, close, open } = useModal();
  const radioInputEl = useCallback(({ closeFn }: { closeFn?: () => void }) => <CommonInput close={closeFn} />, []);
  return (
    <>
      <div tw="hidden md:block">
        <CustomDropdown
          show={show}
          menuItemEl={radioInputEl({ closeFn: close })}
          menuButtonEl={<SortButtonEl open={open} disabled={disabled} />}
          toggle={show ? close : open}
          disabled={disabled}
        />
      </div>
      <SortDrawer radioInputEl={radioInputEl} />
    </>
  );
};
export default SortDropdown;
const SortDrawer = memo(
  ({ radioInputEl }: { radioInputEl: ({ closeFn }: { closeFn?: () => void }) => JSX.Element }) => {
    const { show, close, open } = useModal();
    return (
      <div tw="block md:hidden relative">
        <BaseModal
          {...{
            open: show,
            onClose: close,
            layout: 'drawer',
            showMobileGestures: true,
            title: <Title content="Ordina i risultati" standardVariant="h4" isCenter />,
          }}
        >
          {radioInputEl({ closeFn: close })}
        </BaseModal>
        <SortButtonEl open={open} />
      </div>
    );
  }
);
const CommonInput = memo(({ close }: { close: () => void }) => {
  const setJobListParams = useSetAtom(jobListParamsAtom);
  const [sortFilter, setSortFilter] = useAtom(jobSortFilterAtom);
  const { isDesktop } = useResponsiveBreakPoints();
  const handleChange = useCallback(
    async (value: string) => {
      setSortFilter(value);
      setJobListParams((p) => {
        const newPayload = {
          ...p,
          offset: 0,
          ...JSON.parse(value),
        };
        return newPayload;
      });
      close();
    },
    [close, setJobListParams, setSortFilter]
  );
  const sortList = useJobExplorerSortMenuList();
  return (
    <RadioGroup
      showVerticalLineBelow={!isDesktop}
      options={[...sortList]}
      onChange={handleChange}
      borderless
      id="sort-dropdown"
      returnKey="key"
      value={sortFilter}
      twStyle={tw`max-w-xs`}
    />
  );
});
const SortButtonEl = memo(({ open, disabled }: { open?: () => void; disabled?: boolean }) => {
  const { t: translateCommon } = useTranslation('common');
  const sortFilter = useAtomValue(jobSortFilterAtom);
  const sortList = useJobExplorerSortMenuList();
  const selectedLabel = React.useMemo(
    () => [...sortList].find((v) => v.key === sortFilter)?.label,
    [sortFilter, sortList]
  );
  return (
    <div
      aria-label="sort-button"
      className={`flex items-center gap-1 border border-gray-300 bg-white rounded-lg p-2 cursor-pointer max-h-[40px] ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      onClick={open}
    >
      <div tw="flex shrink-0 items-center">
        <a tw="text-base text-black font-normal lg:(font-semibold text-sm)" href="#!">
          {translateCommon('order')}
        </a>
        <span tw="hidden md:block">:</span>
      </div>
      {selectedLabel ? (
        <div tw="hidden md:block shrink-0">
          <Paragraph standardVariant="body-sm" font="bold" isPointer>
            {' '}
            {`${selectedLabel}`}
          </Paragraph>
        </div>
      ) : null}
      <BarsArrowDownIcon className="h-6 w-6 text-gray-900 shrink-0" />
    </div>
  );
});
export const useJobExplorerSortMenuList = () => {
  const { t: translateCommon } = useTranslation('common');

  const sortList = useMemo(
    () => [
      {
        id: 'published_at',
        label: translateCommon('from_most_recent'),
        key: '{"order_by": ["desc:published_at"]}',
      },
      {
        id: 'published_at',
        label: translateCommon('from_the_oldest'),
        key: '{"order_by": ["asc:published_at"]}',
      },
      {
        id: 'salary',
        label: translateCommon('higher_salary'),
        key: '{"order_by": ["desc:salary"]}',
      },
      {
        id: 'salary',
        label: translateCommon('lower_salary'),
        key: '{"order_by": ["asc:salary"]}',
      },
    ],
    [translateCommon]
  );
  return sortList;
};
